// Blog-related utility functions

/**
 * Generate URL for a blog post
 */
export const getBlogPostUrl = (post: { slug: string } | string) => {
  const slug = typeof post === 'string' ? post : post.slug;
  return `/${process.env.NEXT_PUBLIC_BLOG_SLUG}/${slug}`;
};

/**
 * Generate URL for an author page
 */
export const getAuthorUrl = (author?: { slug: string } | null) => {
  if (!author) return null;
  return `/${process.env.NEXT_PUBLIC_BLOG_SLUG}/${process.env.NEXT_PUBLIC_BLOG_AUTHOR_SLUG}/${author.slug}`;
};

/**
 * Generate URL for a category page
 */
export const getCategoryUrl = (category: { slug: string }) => {
  return `/${process.env.NEXT_PUBLIC_BLOG_SLUG}/${process.env.NEXT_PUBLIC_BLOG_CATEGORY_SLUG}/${category.slug}`;
};

/**
 * Generate URL for a tag page
 */
export const getTagUrl = (tag: { slug: string }) => {
  return `/${process.env.NEXT_PUBLIC_BLOG_SLUG}/${process.env.NEXT_PUBLIC_BLOG_TAG_SLUG}/${tag.slug}`;
};

/**
 * Generate URL for a blog search page
 */
export const getBlogSearchUrl = (query?: string) => {
  const baseUrl = `/${process.env.NEXT_PUBLIC_BLOG_SLUG}/${process.env.NEXT_PUBLIC_BLOG_SEARCH_SLUG}`;
  return query ? `${baseUrl}?q=${encodeURIComponent(query)}` : baseUrl;
};

/**
 * Generate URL for a blog archive page
 */
export const getBlogArchiveUrl = (date: string) => {
  return `/${process.env.NEXT_PUBLIC_BLOG_SLUG}/${process.env.NEXT_PUBLIC_BLOG_ARCHIVE_SLUG}/${date}`;
};

/**
 * Generate main blog index URL
 */
export const getBlogUrl = () => {
  return `/${process.env.NEXT_PUBLIC_BLOG_SLUG}`;
};

/**
 * Format date for blog posts
 */
export const formatBlogDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * Format archive date for display
 */
export const formatArchiveDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
  });
};

/**
 * Extract excerpt from HTML content
 */
export const getExcerpt = (content: string, maxLength = 150) => {
  // Remove HTML tags and get plain text
  const plainText = content.replace(/<[^>]*>/g, '');
  
  if (plainText.length <= maxLength) {
    return plainText;
  }
  
  // Truncate at word boundary
  const truncated = plainText.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  
  return lastSpace > 0 ? truncated.substring(0, lastSpace) + '...' : truncated + '...';
};

// Legacy function name aliases for backward compatibility
export const getBlogCategoryUrl = getCategoryUrl;
export const getBlogAuthorUrl = (slug: string) => getAuthorUrl({ slug });
export const getBlogTagUrl = getTagUrl;